import { type QueryClient, useQueryClient } from "@tanstack/solid-query";
import { createFileRoute } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { type JSX } from "solid-js";
import { PasswordSecurityCard } from "~/components/auth/password-form";
import { IconShield, IconUser } from "~/components/icons";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
} from "~/components/standard-route/detail/layout";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { CardContent } from "~/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { passwordStatusResource } from "~/features/account/password.resource";
import { authFunctionMiddleware } from "~/server/middleware/auth";

const getProfileUser = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .handler(async ({ context }) => context.user);

export const Route = createFileRoute("/_dashboard/dashboard/profile")({
  staticData: {
    breadcrumb: "Profile",
  },
  loader: async ({ context }: { context: { queryClient: QueryClient } }) => {
    const [user, hasPassword] = await Promise.all([
      getProfileUser(),
      context.queryClient.ensureQueryData(
        passwordStatusResource.options(undefined),
      ),
    ]);
    return { user, hasPassword };
  },
  component: ProfilePage,
});

function ProfilePage() {
  const qc = useQueryClient();
  const data = Route.useLoaderData();
  const user = () => data().user;
  const initials = () =>
    user()
      ?.name?.split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase() || "U";

  passwordStatusResource.seedIfEmpty(qc, undefined, data().hasPassword);

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-10">
      <div class="shrink-0 space-y-4">
        <div class="flex items-center gap-4">
          <Avatar class="size-12 shrink-0 border border-border/60">
            <AvatarImage
              src={user()?.image ?? undefined}
              alt={user()?.name ?? undefined}
            />
            <AvatarFallback class="text-sm">{initials()}</AvatarFallback>
          </Avatar>
          <div class="min-w-0">
            <h4 class="wrap-break-word text-xl font-semibold truncate">
              {user()?.name ?? "Unnamed user"}
            </h4>
            <p class="text-sm text-muted-foreground truncate">
              {user()?.email ?? "No email"}
            </p>
          </div>
        </div>
      </div>

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          <StandardRouteDetailLeftColumn>
            <StandardRouteDetailCard
              icon={<IconUser class="size-5" />}
              title="Account"
              contentClass="divide-y divide-border"
            >
              <InfoRow label="Name" value={user()?.name} />
              <InfoRow label="Email" value={user()?.email} />
            </StandardRouteDetailCard>
          </StandardRouteDetailLeftColumn>

          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader class="pb-0">
                <Tabs defaultValue="security">
                  <TabsList class="w-full">
                    <TabsTrigger value="security" class="flex-1">
                      <IconShield class="mr-2 size-4" />
                      Security
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="security">
                    <CardContent class="px-0 pt-4 pb-6">
                      <PasswordSecurityCard />
                    </CardContent>
                  </TabsContent>
                </Tabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>
    </div>
  );
}

function InfoRow(props: {
  label: string;
  value: string | null | undefined;
}): JSX.Element {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "N/A"}</h6>
    </div>
  );
}
